/**
 * Componente para subir y mostrar avatar/imagen de perfil
 */

import { useState, useRef, useCallback } from "react";
import { Camera, Upload, Loader2, X, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { uploadProfileImage, validateImageFile } from "@/lib/services/storage";
import { toast } from "react-hot-toast";

interface AvatarUploadProps {
  /** URL de la imagen actual */
  currentImageUrl?: string | null;
  /** Nombre para mostrar iniciales como fallback */
  name?: string;
  /** Tipo de entidad para organizar en storage */
  entityType: "profiles" | "members" | "users";
  /** ID de la entidad */
  entityId: string;
  /** Callback cuando se sube una imagen exitosamente */
  onUpload: (url: string) => void;
  /** Callback cuando se elimina la imagen */
  onRemove?: () => void;
  /** Tamaño del avatar */
  size?: "sm" | "md" | "lg" | "xl";
  /** Deshabilitar la edición */
  disabled?: boolean;
  /** Clase CSS adicional */
  className?: string;
}

const sizeClasses = {
  sm: "w-16 h-16",
  md: "w-24 h-24",
  lg: "w-32 h-32",
  xl: "w-40 h-40",
};

const iconSizes = {
  sm: "w-4 h-4",
  md: "w-5 h-5",
  lg: "w-6 h-6",
  xl: "w-8 h-8",
};

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function getColorFromName(name: string): string {
  const colors = [
    "from-blue-500 to-blue-600",
    "from-green-500 to-green-600",
    "from-purple-500 to-purple-600",
    "from-orange-500 to-orange-600",
    "from-pink-500 to-pink-600",
    "from-teal-500 to-teal-600",
    "from-indigo-500 to-indigo-600",
    "from-rose-500 to-rose-600",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
}

export function AvatarUpload({
  currentImageUrl,
  name = "",
  entityType,
  entityId,
  onUpload,
  onRemove,
  size = "lg",
  disabled = false,
  className,
}: AvatarUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const displayUrl = previewUrl || currentImageUrl;

  const handleFileSelect = useCallback(
    async (file: File) => {
      // Validar archivo
      const validation = validateImageFile(file);
      if (!validation.valid) {
        toast.error(validation.error || "Archivo no válido");
        return;
      }

      // Mostrar preview
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);

      // Subir archivo
      setIsUploading(true);
      try {
        const result = await uploadProfileImage(file, entityType, entityId);
        onUpload(result.url);
        toast.success("Imagen actualizada correctamente");
      } catch (error) {
        console.error("Error uploading image:", error);
        toast.error("Error al subir la imagen");
        setPreviewUrl(null);
      } finally {
        setIsUploading(false);
        URL.revokeObjectURL(objectUrl);
      }
    },
    [entityType, entityId, onUpload],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    // Reset input para permitir seleccionar el mismo archivo
    e.target.value = "";
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      if (disabled) return;

      const file = e.dataTransfer.files?.[0];
      if (file && file.type.startsWith("image/")) {
        handleFileSelect(file);
      }
    },
    [disabled, handleFileSelect],
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleRemove = async () => {
    if (onRemove) {
      onRemove();
      setPreviewUrl(null);
    }
  };

  const handleClick = () => {
    if (!disabled && !isUploading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className={cn("relative inline-block", className)}>
      {/* Input oculto */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled || isUploading}
      />

      {/* Contenedor principal */}
      <div
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={cn(
          "relative rounded-full overflow-hidden cursor-pointer group",
          "transition-all duration-300 ease-out",
          sizeClasses[size],
          isDragging && "ring-4 ring-blue-500/50 scale-105",
          disabled && "cursor-not-allowed opacity-60",
          !disabled && "hover:shadow-lg hover:scale-[1.02]",
        )}
      >
        {/* Imagen o fallback */}
        {displayUrl ? (
          <img
            src={displayUrl}
            alt={name || "Avatar"}
            className="w-full h-full object-cover"
          />
        ) : (
          <div
            className={cn(
              "w-full h-full flex items-center justify-center",
              "bg-gradient-to-br text-white font-medium",
              name ? getColorFromName(name) : "from-gray-400 to-gray-500",
            )}
          >
            {name ? (
              <span
                className={cn(
                  size === "sm" && "text-sm",
                  size === "md" && "text-lg",
                  size === "lg" && "text-2xl",
                  size === "xl" && "text-3xl",
                )}
              >
                {getInitials(name)}
              </span>
            ) : (
              <User className={iconSizes[size]} />
            )}
          </div>
        )}

        {/* Overlay de hover/loading */}
        <div
          className={cn(
            "absolute inset-0 flex items-center justify-center",
            "bg-black/50 transition-opacity duration-200",
            isUploading ? "opacity-100" : "opacity-0 group-hover:opacity-100",
          )}
        >
          {isUploading ? (
            <Loader2
              className={cn("text-white animate-spin", iconSizes[size])}
            />
          ) : (
            <div className="flex flex-col items-center gap-1">
              <Camera className={cn("text-white", iconSizes[size])} />
              <span className="text-white text-xs font-medium">Cambiar</span>
            </div>
          )}
        </div>
      </div>

      {/* Botón de eliminar */}
      {displayUrl && onRemove && !disabled && !isUploading && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleRemove();
          }}
          className={cn(
            "absolute -top-1 -right-1 p-1.5 rounded-full",
            "bg-red-500 text-white shadow-lg",
            "hover:bg-red-600 transition-colors duration-200",
            "focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2",
          )}
          title="Eliminar imagen"
        >
          <X className="w-3 h-3" />
        </button>
      )}

      {/* Indicador de drag */}
      {isDragging && (
        <div
          className={cn(
            "absolute inset-0 rounded-full",
            "bg-blue-500/20 border-2 border-dashed border-blue-500",
            "flex items-center justify-center pointer-events-none",
          )}
        >
          <Upload className="w-6 h-6 text-blue-500" />
        </div>
      )}
    </div>
  );
}

export default AvatarUpload;
